import { useCallback, useEffect, useState } from 'react';
import { api, type DemoDescriptor, type DemoStatus } from '../lib/api';
import { navigate } from '../lib/nav';

const AVAIL_PILL: Record<DemoStatus['available'], string> = {
  available: 'ok',
  'needs-setup': 'warn',
  unavailable: 'off',
};
const AVAIL_LABEL: Record<DemoStatus['available'], string> = {
  available: 'Available',
  'needs-setup': 'Needs setup',
  unavailable: 'Unavailable',
};

/**
 * Per-system Demos panel: pick a capability demo, name + attach a Unity Catalog for its shared data,
 * see the end-to-end (AIStor → Databricks) validation checklist + "available" verdict, and run the
 * walkthrough. Databricks credentials live on the Integrations page (per user) — the demo is visible
 * but gated until the required integration is connected.
 */
export function DemoPanel({ systemId, running }: { systemId: string; running: boolean }) {
  const [demos, setDemos] = useState<DemoDescriptor[]>([]);
  const [statuses, setStatuses] = useState<Record<string, DemoStatus>>({});
  const [selected, setSelected] = useState<string>('');
  const [catalog, setCatalog] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dbx, setDbx] = useState<{ host: string; configured: boolean } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [{ demos }, { statuses }, dbxCfg] = await Promise.all([
        api.demos(),
        api.systemDemos(systemId),
        api.databricksConfig().catch(() => ({ host: '', configured: false })),
      ]);
      setDemos(demos);
      setStatuses(Object.fromEntries(statuses.map((s) => [s.demoId, s])));
      setSelected((cur) => cur || demos[0]?.id || '');
      setDbx(dbxCfg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load demos');
    }
  }, [systemId]);

  useEffect(() => {
    void load();
  }, [load]);

  const demo = demos.find((d) => d.id === selected);
  const status = selected ? statuses[selected] : undefined;

  // Prefill the catalog + warehouse fields from the current attachment.
  useEffect(() => {
    if (!status) return;
    setCatalog((c) => c || status.databricksCatalog || '');
    setWarehouse((w) => w || status.warehouse || '');
  }, [status]);

  const validate = useCallback(async () => {
    if (!demo) return;
    setBusy(true);
    setError(null);
    try {
      const { status } = await api.validateDemo(systemId, demo.id);
      setStatuses((s) => ({ ...s, [demo.id]: status }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'validation failed');
    } finally {
      setBusy(false);
    }
  }, [demo, systemId]);

  const attach = useCallback(async () => {
    if (!demo) return;
    setBusy(true);
    setError(null);
    try {
      await api.attachDemo(systemId, demo.id, { databricksCatalog: catalog.trim() || undefined, warehouse: warehouse.trim() || undefined });
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'attach failed';
      setError(
        msg === 'invalid_catalog_name'
          ? 'Catalog name must be letters, digits, or underscore.'
          : msg === 'invalid_warehouse_name'
            ? 'Warehouse name must be lowercase letters, digits, and hyphens (no underscores), and not “minio”/“aistor” (reserved).'
            : msg,
      );
    } finally {
      setBusy(false);
    }
  }, [demo, systemId, catalog, warehouse, load]);

  if (!demo) return <div className="reveal">{error ? <p className="error">{error}</p> : 'Loading demos…'}</div>;

  // Integration gate: the demo is visible, but can't proceed until its required integrations are live.
  const needsDatabricks = (demo.requiresIntegrations ?? []).includes('databricks');
  const dbxLive = !!dbx?.configured;
  const gated = needsDatabricks && !dbxLive;

  const demoHref = (mode: 'run' | 'teardown') =>
    `/demo?system=${encodeURIComponent(systemId)}&demo=${encodeURIComponent(demo.id)}${mode === 'teardown' ? '&mode=teardown' : ''}`;

  return (
    <div className="reveal demo-panel">
      <div className="demo-head">
        <select value={selected} onChange={(e) => setSelected(e.target.value)} className="demo-select">
          {demos.map((d) => (
            <option key={d.id} value={d.id}>
              {d.label}
            </option>
          ))}
        </select>
        {status && <span className={`pill ${AVAIL_PILL[status.available]}`}>{AVAIL_LABEL[status.available]}</span>}
      </div>

      <p className="muted small-note">{demo.description}</p>

      {needsDatabricks &&
        (gated ? (
          <div className="gate">
            <div className="gate-main">
              <span className="gate-title">Databricks integration required</span>
              <span className="muted small-note">
                Connect a Databricks workspace to enable this demo. Credentials are set once, per account.
              </span>
            </div>
            <button type="button" className="btn primary" onClick={() => navigate('/integrations')}>
              Go to Integrations →
            </button>
          </div>
        ) : (
          <p className="small-note gate-ok">
            <span className="mark">✓</span> Databricks integration connected
            {dbx?.host ? ` · ${dbx.host.replace(/^https:\/\//, '')}` : ''} ·{' '}
            <button type="button" className="link gate-link" onClick={() => navigate('/integrations')}>
              manage
            </button>
          </p>
        ))}

      <div className="demo-attach">
        {/* Warehouse + catalog only apply to the Databricks (Tables/Delta Sharing) demo. */}
        {needsDatabricks && (
          <>
            <label className="field">
              <span>AIStor warehouse (lowercase, digits, hyphens)</span>
              <input value={warehouse} onChange={(e) => setWarehouse(e.target.value)} placeholder="aistor-tables-warehouse" />
            </label>
            <label className="field">
              <span>Databricks catalog name (created for this system)</span>
              <input value={catalog} onChange={(e) => setCatalog(e.target.value)} placeholder="fa_demo_bakehouse" />
            </label>
          </>
        )}
        <button type="button" className="btn ghost" disabled={busy || gated} onClick={() => void attach()}>
          {status?.attached ? 'Update' : 'Attach'}
        </button>
      </div>

      {!running && <p className="small-note">Start the system to validate and run this demo.</p>}

      {status && (
        <ul className="checklist">
          {status.checks.map((c) => (
            <li key={c.id} className={c.ok ? 'ok' : 'no'}>
              <span className="mark">{c.ok ? '✓' : '○'}</span>
              <span className="clabel">{c.label}</span>
              <span className="muted cdetail">{c.detail}</span>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="error">{error}</p>}

      <div className="actions">
        <button type="button" className="btn ghost" disabled={busy || !running || gated} onClick={() => void validate()}>
          {busy ? 'Working…' : 'Validate'}
        </button>
        <a
          className={`btn primary ${running && !gated ? '' : 'disabled'}`}
          href={running && !gated ? demoHref('run') : undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!running || gated}
          title={gated ? 'Connect Databricks on the Integrations page first' : running ? 'Opens the walkthrough in a new tab' : 'Start the system first'}
        >
          Open walkthrough ↗
        </a>
        <a
          className={`btn ghost ${gated ? 'disabled' : ''}`}
          href={gated ? undefined : demoHref('teardown')}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={gated}
          title="Tear down only the Databricks + share resources this demo created"
        >
          Teardown ↗
        </a>
      </div>
    </div>
  );
}
